// /go/demo?mode=solo|couple&autoplay=1&v=2 — Ghost Driver web-player.
// Speelt het originele script (GET /api/demo/auto/script) stap per stap af
// door de nagebouwde app-schermen in een PhoneFrame. Read-only demo.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDemoTracking } from '../useDemoTracking';
import { screenFor, TabBar } from './screens';
import { PhoneFrame } from './PhoneFrame';
import {
  ControlBar, GhostToast, EmailPreview, IntelligenceReveal,
  WooncoachBubble, AutoBadge, EndScreen, IntroBanner,
} from './Overlays';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GhostDriver() {
  const [sp] = useSearchParams();
  const mode = sp.get('mode') === 'couple' ? 'couple' : 'solo';
  const version = sp.get('v') === '1' ? '1' : '2';
  const autoplay = sp.get('autoplay') === '1';
  const source = ['cta', 'qr', 'website', 'other'].includes(sp.get('source')) ? sp.get('source') : 'website';
  const isV2 = version === '2';

  const { track } = useDemoTracking('ghost-demo');
  const [steps, setSteps] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [idx, setIdx] = useState(0);
  const [shownIdx, setShownIdx] = useState(0);
  const [status, setStatus] = useState('idle');
  const [intro, setIntro] = useState(false);
  const [toast, setToast] = useState(null);
  const [bubble, setBubble] = useState(null);
  const [email, setEmail] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [end, setEnd] = useState(null);

  const stepsRef = useRef([]);
  const idxRef = useRef(0);
  const statusRef = useRef('idle');
  const timerRef = useRef(null);
  const introTimerRef = useRef(null);
  const subTimers = useRef([]);
  const startedRef = useRef(false);
  const scrollRef = useRef(null);

  const hideIntroSoon = useCallback(() => {
    if (introTimerRef.current) clearTimeout(introTimerRef.current);
    introTimerRef.current = setTimeout(() => setIntro(false), 1600);
  }, []);

  const clearAll = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    subTimers.current.forEach(clearTimeout);
    subTimers.current = [];
  }, []);

  const later = useCallback((fn, ms) => {
    subTimers.current.push(setTimeout(fn, ms));
  }, []);

  const showStep = useCallback((i) => {
    const stepsArr = stepsRef.current;
    if (!stepsArr.length) return;
    const cur = Math.max(0, Math.min(i, stepsArr.length - 1));
    clearAll();
    idxRef.current = cur;
    setIdx(cur);
    setToast(null); setBubble(null); setEmail(null); setReveal(null);

    const step = stepsArr[cur];
    const type = step.type || 'default';
    const dur = Math.max(2500, step.duration_ms || 3500);
    track('ghost_step', { step: step.id, title: step.title, deeplink: step.deeplink, type, mode });

    if (type !== 'intelligence_reveal' && type !== 'endscreen') {
      setShownIdx(cur);
      try { scrollRef.current?.scrollTo({ top: 0 }); } catch {}
    }

    if (type === 'endscreen') {
      setEnd({ title: step.title, subtitle: step.subtitle, crossMode: step.cross_mode || null });
      statusRef.current = 'finished';
      setStatus('finished');
      track('end_open', { module: 'ghost-demo', mode });
      return;
    }
    setEnd(null);

    if (step.toast) {
      setToast(step.toast);
      later(() => setToast(null), Math.min(5000, Math.max(3000, dur - 500)));
    }
    if (step.email_preview) {
      setEmail(step.email_preview);
      later(() => setEmail(null), Math.max(3500, dur - 300));
    }
    if (type === 'intelligence_reveal') {
      setReveal({ title: step.title, subtitle: step.subtitle });
      later(() => setReveal(null), Math.max(3200, dur - 200));
    }
    if (step.wooncoach_bubble) {
      later(() => setBubble(step.wooncoach_bubble), Math.max(600, Math.min(1400, dur * 0.28)));
    }
    if (Array.isArray(step.autoscroll)) {
      step.autoscroll.forEach((p) => later(() => {
        try { scrollRef.current?.scrollTo({ top: p.to, behavior: 'smooth' }); } catch {}
      }, p.delay));
    }
    if (step.driver && step.driver.action === 'swipe') {
      later(() => {
        try {
          window.dispatchEvent(new CustomEvent('demo:swipe', {
            detail: { decision: step.driver.decision, actor: step.driver.actor || null },
          }));
        } catch {}
      }, step.driver.delay_ms || 1200);
    }
    if (statusRef.current === 'running') {
      timerRef.current = setTimeout(() => {
        if (statusRef.current === 'running') showStep(cur + 1);
      }, dur);
    }
  }, [clearAll, later, track, mode]);

  const start = useCallback(() => {
    if (startedRef.current && statusRef.current === 'running') return;
    startedRef.current = true;
    statusRef.current = 'running';
    setStatus('running');
    setIntro(true);
    hideIntroSoon();
    timerRef.current = setTimeout(() => showStep(0), 900);
  }, [hideIntroSoon, showStep]);

  const pause = useCallback(() => { clearAll(); statusRef.current = 'paused'; setStatus('paused'); }, [clearAll]);
  const resume = useCallback(() => {
    if (statusRef.current === 'finished') return;
    statusRef.current = 'running'; setStatus('running');
    showStep(idxRef.current);
  }, [showStep]);
  const next = useCallback(() => {
    if (statusRef.current === 'finished') return;
    statusRef.current = 'running'; setStatus('running');
    showStep(idxRef.current + 1);
  }, [showStep]);
  const previous = useCallback(() => {
    if (!isV2) return;
    statusRef.current = 'paused'; setStatus('paused');
    setEnd(null);
    showStep(idxRef.current - 1);
  }, [isV2, showStep]);
  const reset = useCallback(() => {
    statusRef.current = 'running'; setStatus('running');
    setEnd(null);
    setIntro(true);
    hideIntroSoon();
    timerRef.current = setTimeout(() => showStep(0), 600);
  }, [hideIntroSoon, showStep]);
  const stop = useCallback(() => { clearAll(); window.location.href = '/'; }, [clearAll]);

  const handleEndAction = useCallback((action) => {
    if (action === 'replay') { reset(); return; }
    if (action === 'switch_mode') {
      const other = mode === 'couple' ? 'solo' : 'couple';
      window.location.href = `/go/demo?mode=${other}&autoplay=1&v=2`;
      return;
    }
    if (action === 'open_app') {
      track('app_cta_clicked', { from: 'ghost_endscreen', mode });
      window.location.href = '/app-demo';
      return;
    }
    if (action === 'back_to_site') {
      window.location.href = 'https://www.immo-match.be';
    }
  }, [mode, reset, track]);

  // Init: track klik + haal script op
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/analytics/demo-track`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source, meta: { mode, v: version, autoplay } }),
    }).catch(() => {});
    track('demo_started', { module: 'ghost-demo', mode, v: version, autoplay });
    track('mode_selected', { mode, via: 'ghost-demo' });

    fetch(`${API}/demo/auto/script?mode=${mode}&v=${version}`)
      .then((r) => { if (!r.ok) throw new Error('script'); return r.json(); })
      .then((d) => {
        if (cancelled) return;
        stepsRef.current = d.steps || [];
        setSteps(d.steps || []);
        if (autoplay) start();
        else {
          statusRef.current = 'paused';
          setStatus('paused');
          setIntro(true);
          showStep(0);
        }
      })
      .catch(() => { if (!cancelled) setLoadError('Kon het demo-script niet laden.'); });
    return () => { cancelled = true; clearAll(); };
  }, []); // eslint-disable-line

  const current = steps ? steps[Math.min(idx, steps.length - 1)] : null;
  const shownStep = steps ? steps[Math.min(shownIdx, steps.length - 1)] : null;
  const scr = shownStep ? screenFor(shownStep, { mode }) : null;
  // Remount enkel bij een ander deeplink — zo behoudt een scherm (bv. de swipe-stapel)
  // zijn lokale state over opeenvolgende stappen van hetzelfde scherm.
  const screenKey = shownStep ? (shownStep.deeplink || '').split('?')[0] : 'none';
  const finished = status === 'finished';
  const label = finished ? 'Demo voltooid 🎯' : `${Math.min(idx + 1, steps?.length || 1)}/${steps?.length || '-'}`;
  const showAutoBadge = !!(current && current.auto_badge && !end);

  return (
    <PhoneFrame>
      <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#EEF3F9' }} data-testid="ghost-driver">
        {steps === null && !loadError ? (
          <div style={{ flex: 1, background: '#0e2340', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/logo_official.png" alt="IMMO MATCH" style={{ width: 96, height: 96, objectFit: 'contain', animation: 'immoLogoPulse 1.4s ease infinite' }} />
          </div>
        ) : null}

        {loadError ? (
          <div style={{ flex: 1, background: '#0e2340', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 800, marginBottom: 6 }}>De demo kon niet laden</div>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 16 }}>{loadError}</div>
              <button className="immo-btn" data-testid="ghost-retry" onClick={() => window.location.reload()} style={{ background: '#c48a3a', color: '#0e2340', fontWeight: 900, fontSize: 13, padding: '11px 20px', borderRadius: 999, margin: '0 auto' }}>
                Opnieuw proberen
              </button>
            </div>
          </div>
        ) : null}

        {steps ? (
          <>
            <div ref={scrollRef} data-testid="ghost-screen" style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
              {scr ? <div key={screenKey} style={{ height: '100%' }}>{scr.node}</div> : null}
            </div>
            {scr?.tab ? <TabBar active={scr.tab} /> : null}

            <IntroBanner show={intro} isV2={isV2} autoplay={autoplay} onStart={start} />
            <GhostToast toast={toast} />
            <AutoBadge show={showAutoBadge} />
            <IntelligenceReveal reveal={reveal} mode={mode} />
            <EmailPreview email={email} onClose={() => setEmail(null)} />
            <WooncoachBubble bubble={bubble} />
            <EndScreen end={end} onAction={handleEndAction} />
            <ControlBar
              isV2={isV2}
              status={status}
              label={label}
              current={current}
              finished={finished}
              onPrev={previous}
              onPlayPause={status === 'running' ? pause : resume}
              onNext={next}
              onReset={reset}
              onStop={stop}
              mode={mode}
            />
          </>
        ) : null}
      </div>
    </PhoneFrame>
  );
}
